import React from 'react';

import { FaEnvelope } from '@react-icons/all-files/fa/FaEnvelope';
import { FaMapMarkerAlt } from '@react-icons/all-files/fa/FaMapMarkerAlt';
import { FaPhone } from '@react-icons/all-files/fa/FaPhone';
import { graphql, PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { ContactForm } from '../components/ContactForm/ContactForm';
import { Heading } from '../components/Heading/Heading';
import { Layout } from '../components/Layout/Layout';
import { SearchEngineOptimization } from '../components/SearchEngineOptimization/SearchEngineOptimization';
import { SpeculationRules } from '../components/SpeculationRules';
import { SUBJECT_QUERY_PARAM } from '../misc/contact';
import { BreadCrumbItem, Url } from '../misc/enums';

type Data = any;

const Kontakt = ({ data, location }: PageProps<Data>) => {
	const {
		contactPage: { frontmatter: contactPage },
		site: {
			siteMetadata: {
				organization: { address, contactPoint },
			},
		},
	} = data;

	const searchParams = new URLSearchParams(location.search);
	const defaultSubject = searchParams.get(SUBJECT_QUERY_PARAM) ?? undefined;

	const contactOnSuccess = () => {
		window.plausible?.(
			'contact_form_sent',
			window?.previousLocation?.pathname
				? {
						props: { previous_page: window.previousLocation.pathname },
					}
				: undefined,
		);
	};

	return (
		<Layout as="main" showContactSectionInFooter={false}>
			<section className="container pb-12 md:py-16">
				<div className="flex flex-col items-center gap-3">
					<Heading
						as="h1"
						className="max-w-[60ch] text-balance text-center text-3xl font-semibold text-secondary-700 md:text-5xl"
					>
						{contactPage.title}
					</Heading>
					<p className="max-w-[70ch] text-pretty text-center text-lg text-secondary-700">
						{contactPage.intro}
					</p>
				</div>
			</section>

			<section className="relative">
				<div className="container">
					<div className="rounded-lg bg-white p-5 shadow-lg md:p-12">
						<ContactForm
							defaultValues={{ interesse: defaultSubject }}
							onSuccess={contactOnSuccess}
						/>
					</div>
					<div className="absolute inset-x-0 bottom-24 top-44 -z-20 opacity-35">
						<StaticImage
							src="../../static/images/hero_background.png"
							alt=""
							className="h-full w-full"
						/>
					</div>
				</div>
			</section>

			<section className="container my-24">
				<div className="relative flex flex-col gap-8 lg:pl-16">
					{/* overflow hidden is a hack to prevent the location box on the top right to be visible */}
					<div className="h-[600px] overflow-hidden rounded-lg">
						<iframe
							src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2369.800483956409!2d9.957335316204246!3d53.561328980024605!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b18f42a6368d57%3A0xed7f8ad3fcad8e40!2seasy%20data%20solution%20GmbH!5e0!3m2!1sde!2sde!4v1588828550241!5m2!1sde!2sde"
							title="Anfahrt easy data solution"
							width="100%"
							height="800"
							frameBorder="0"
							allowFullScreen={false}
							aria-hidden="false"
							loading="lazy"
							className="-mt-[200px]"
						/>
					</div>
					<div className="rounded-lg bg-white px-6 py-8 shadow-md lg:absolute lg:left-0 lg:top-1/2 lg:max-w-sm lg:-translate-y-1/2">
						<h2 className="text-2xl font-semibold text-secondary-700">Info</h2>
						<p className="mt-2 text-lg text-secondary-700">
							Jetzt einfach und unkompliziert Kontakt aufnehmen.
						</p>

						<div className="mt-8 flex flex-col space-y-6">
							<div className="flex gap-2">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white">
									<FaMapMarkerAlt />
								</div>
								<div>
									<p className="font-semibold leading-tight text-secondary-700">Adresse</p>
									<p className="leading-tight text-secondary-700">
										{address.streetAddress}, {address.postalCode} {address.addressLocality}
									</p>
								</div>
							</div>

							<div className="flex gap-2">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white">
									<FaPhone />
								</div>
								<div>
									<p className="font-semibold leading-tight text-secondary-700">Telefon</p>
									<p className="leading-tight text-secondary-700">{contactPoint.telephone}</p>
								</div>
							</div>

							<div className="flex gap-2">
								<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-500 text-white">
									<FaEnvelope />
								</div>
								<div>
									<p className="font-semibold leading-tight text-secondary-700">E-Mail</p>
									<p className="leading-tight text-secondary-700">{contactPoint.email}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>
		</Layout>
	);
};

export default Kontakt;

export const Head = ({ data }) => {
	const {
		contactPage: { frontmatter: contactPage },
	} = data;

	const path = `/${Url.CONTACT}/`;
	const breadcrumbItems = [BreadCrumbItem.HOME, BreadCrumbItem.CONTACT];

	return (
		<>
			<SpeculationRules />
			<SearchEngineOptimization
				title={contactPage.seo.title}
				description={contactPage.seo.description}
				path={path}
				breadcrumbItems={breadcrumbItems}
			/>
		</>
	);
};

export const pageQuery = graphql`
	query Contact {
		contactPage: markdownRemark(fileAbsolutePath: { regex: "/kontakt.md/" }) {
			frontmatter {
				seo {
					title
					description
				}
				title
				intro
			}
		}

		site {
			siteMetadata {
				xingUrl
				organization {
					legalName
					address {
						streetAddress
						addressLocality
						postalCode
					}
					contactPoint {
						telephone
						email
					}
				}
			}
		}
	}
`;
